import { IApiClientOptions, IRequestProps } from 'core';
import { IDeviceGroupOptions, TCreateDeviceGroupOptions, TUpdateDeviceGroupOptions, IMoveDeviceToGroupResponseOptions, PlatformDto, DeviceGroupDto } from '../dto/device-group-dto';
import { AssureBaseApiClient, HTTP_METHOD } from './assure-base-api-client';
import { IImportCSVResponseOptions, IDeviceAttributesResponse } from 'dto/device-dto';
import { CreateApiFileTransferExecution } from 'dto/file-transfer.dto';
import { transform } from 'dto/transformer';

const devicesRootPathName = 'devicegroups';

export interface IDeviceGroupDataInput {
    [attributeName: string]: string | number | boolean;
}

export class DeviceGroupsApiClient extends AssureBaseApiClient {
    constructor(options: IApiClientOptions) {
        super('devicegroups', options);
    }

    protected getRootPath(): string {
        return devicesRootPathName;
    }

    public async getSupportedPlatforms(accountId: number): Promise<PlatformDto[]> {
        const { supportedPlatforms = [] } = await this.send<{ supportedPlatforms: string[] }>(HTTP_METHOD.GET, this.getRequestOptionsWithAccountId(accountId, 'supported-platforms'));
        return supportedPlatforms.map((platform) => new PlatformDto(platform));
    }

    public async createDeviceGroup(accountId: number, deviceGroupName: string, params: TCreateDeviceGroupOptions): Promise<DeviceGroupDto> {
        const result = await this.send<Object>(HTTP_METHOD.POST, this.getRequestOptionsWithAccountId(accountId, deviceGroupName, params));
        return transform(DeviceGroupDto, result);
    }

    public async updateDeviceGroup(accountId: number, deviceGroupName: string, params: TUpdateDeviceGroupOptions): Promise<boolean> {
        const result = await this.send<{ updated: boolean }>(HTTP_METHOD.PUT, this.getRequestOptionsWithAccountId(accountId, deviceGroupName, params));
        return result.updated;
    }

    public async deleteDeviceGroup(accountId: number, deviceGroupName: string): Promise<boolean> {
        const result = await this.send<{ deleted: boolean }>(HTTP_METHOD.DELETE, this.getRequestOptionsWithAccountId(accountId, deviceGroupName));
        return result.deleted;
    }

    public async moveDevicesToGroup(accountId: number, groupName: string, sourceDeviceGroupName: string, deviceNames: string[]): Promise<IMoveDeviceToGroupResponseOptions[]> {
        const options: IRequestProps = this.getRequestOptionsWithAccountId(
            accountId,
            `${groupName}/move-devices`,
            { sourceDeviceGroupName, deviceNames },
        );
        const result = await this.send<{ devices: IMoveDeviceToGroupResponseOptions[] }>(HTTP_METHOD.PATCH, options);
        return result.devices;
    }

    public async revokeDevicesFromGroup(accountId: number, deviceNames: string[], originDeviceGroupName: string): Promise<IMoveDeviceToGroupResponseOptions[]> {
        const options: IRequestProps = this.getRequestOptionsWithAccountId(accountId, `${originDeviceGroupName}/revoke-devices`, { deviceNames });
        const result = await this.send<{ devices: IMoveDeviceToGroupResponseOptions[] }>(HTTP_METHOD.POST, options);
        return result.devices;
    }

    public async importDevices(accountId: number, clientDeviceGroupName: string, destDeviceGroupName: string, files: File[]): Promise<IImportCSVResponseOptions> {
        return this.send<IImportCSVResponseOptions>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, 'import-devices', { destDeviceGroupName, clientDeviceGroupName, files }),
        );
    }

    async getDeviceGroupDetailsOfAccount(
        accountId: number,
        query?: { hierarchy: 'true' | 'false' | undefined },
    ): Promise<DeviceGroupDto> {
        const options: IRequestProps = {
            url: `/account/${accountId}/devicegroup`,
            params: query,
            options: {
                propagateError: true,
            },
        };
        const result = await this.send<{ devicegroup: IDeviceGroupOptions }>(HTTP_METHOD.GET, options);
        return transform(DeviceGroupDto, result.devicegroup);
    }

    async getDeviceGroupOfAccounts(
        accountId: number,
        query?: { hierarchy: 'true' | 'false' | undefined },
    ): Promise<IDeviceGroupOptions[]> {
        const options: IRequestProps = {
            url: `/account/${accountId}/devicegroup/list`,
            params: query,
            options: {
                propagateError: true,
            },
        };
        const result = await this.send<{ deviceGroups: IDeviceGroupOptions[] }>(HTTP_METHOD.GET, options);
        return result.deviceGroups;
    }

    public getDeviceAttributes(accountId: number, groupName: string): Promise<IDeviceAttributesResponse> {
        return this.send<IDeviceAttributesResponse>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${groupName}/attributes`),
        );
    }

    public transferFiles( accountId: number, deviceGroupName: string, params: CreateApiFileTransferExecution): Promise<string> {
        return this.send<string>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, `${deviceGroupName}/create-file-transfer-execution`, params),
        );
    }

    public async getFileTransferExecutions(accountId: number, deviceGroupName: string): Promise<{ executions: unknown[] }> {
        return this.send<{ executions: unknown[] }>(HTTP_METHOD.GET, this.getRequestOptionsWithAccountId(accountId, `${deviceGroupName}/file-transfer-executions`));
    }

    public async cancelFileTransferExecution(accountId: number, deviceGroupName: string, executionId: string): Promise<{ cancellation_in_progress: boolean }> {
        return this.send<{ cancellation_in_progress: boolean }>(HTTP_METHOD.POST, this.getRequestOptionsWithAccountId(accountId, `${deviceGroupName}/cancel-file-transfer-execution`, { executionId }));
    }
}
